import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of, tap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Countries, DEVELOP_API_LINK, IS_DEVELOPMENT, STORAGE_NAMES } from '@root/app.config';
import * as streamSaver from 'streamsaver';
import {
	ActionData,
	FeedbackData,
	ImageData,
	PriceDetails,
	SendCodeData,
	VerificationResponse,
	VerifyCodeData,
	SessionData,
	AccessResponse,
	RetailerClickAction,
} from '@root/app.interfaces';
import { PaymentIntent } from '@stripe/stripe-js';
import { DISABLE_GLOBAL_EXCEPTION_HANDLING, SUPPRESS_ERROR_MODAL } from '@core/helpers/loader.interseptor';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';

@Injectable({
	providedIn: 'root',
})
export class ServerDataService {
	headers = new HttpHeaders().set('Content-Type', 'application/json');
	countryCode: string;

	private isRetailerKeySentOnInit: boolean;

	constructor(
		private http: HttpClient,
		private sessionStorageService: SessionStorageService,
	) {}

	serverData() {
		const retailerKey = this.getRetailerKey();
		const sessionCountryCode = this.sessionStorageService.getSession(STORAGE_NAMES.zip_country_code);
		const code = !!sessionCountryCode
			? sessionCountryCode
			: !!this.countryCode
				? this.countryCode
				: !!retailerKey
					? Countries[0].code
					: sessionCountryCode;
		const countryData = Countries.filter((item) => item.code === code)[0];
		const apiLink = countryData?.data_store;
		const countryName = countryData?.name.toLocaleLowerCase().split(' ').join('-');

		const link = IS_DEVELOPMENT() ? `${DEVELOP_API_LINK}/${countryName}` : apiLink;
		// const link = IS_DEVELOPMENT() ? `${DEVELOP_API_LINK}/${countryName}/api` : apiLink;

		return {
			main_link: link,
			rest: {
				config: link + '/3dc/configurations',
				config_test: link + '/3dc/configurations-test',
				embedded_linK: link + '/far/retailers/embedded-link',
				retailers: link + '/far/retailers',
				settings: link + '/far/settings',
				outdoor: link + '/far/retailers/product/outdoor',
				access: link + '/far/access',
				sessionData: link + '/3dc/sessions',
				country_code: 'https://get.geojs.io/v1/ip/geo.json',
				price: (session: string) => `${link}/3dc/quotations/${session}`,
				order_pdf: (sessionKey: string) => `${link}/3dc/sessions/download/order-pdf/${sessionKey}/`,
			},
			post: {
				stats: link + '/far/statistics/search/save',
				data: link + '/3dc/save',
				image: link + '/3dc/image/save',
				model_glb: link + '/3dc/model/save',
				email: link + '/3dc/email/email-link',
				feedback: link + '/3dc/feedbacks/save',
				action: link + '/3dc/actions/save',
				retailer_action: link + '/retailers/actions',
				send_code: link + '/3dc/email/verification/send',
				verify_code: link + '/3dc/email/verification/verify',
				payment_intent: link + '/3dc/payments/stripe/create-intent',
			},
			put: {
				sessionData: link + '/3dc/sessions/save',
				download: link + '/3dc/sessions/save-and-download',
			},
			delete: {
				model_glb: link + '/3dc/model/delete',
			},
			link: {
				download: '/3dc/sessions/download/pdf',
			},
		};
	}

	GetServerData(code: string, sessionKey?: string): Observable<any> {
		this.countryCode = code;
		const sessionKeyLink = sessionKey ? '/' + sessionKey : '';
		const retailerKey = this.getRetailerKey();
		const params = !this.isRetailerKeySentOnInit && retailerKey ? { embed_key: retailerKey } : {};

		return this.http.get(this.serverData().rest.config + sessionKeyLink, { params }).pipe(
			tap(() => (this.isRetailerKeySentOnInit = true)),
			catchError(this.errorMgmt),
		);
	}

	async getPdfData(link: string) {
		if (!this.isAppleDevice()) {
			window.open(link, '_blank');
			return of(null);
		}

		const fileStream = streamSaver.createWriteStream('Ziptrak® Blinds – Summary.pdf');
		return await fetch(link)
			.then((res) => {
				const readableStream = res.body;

				if (window.WritableStream && readableStream.pipeTo) {
					return readableStream.pipeTo(fileStream);
				}
			})
			.catch(this.errorMgmt);
	}

	GetRetailersData(): Observable<any> {
		return this.http.get(`${this.serverData().rest.retailers}`).pipe(catchError(this.errorMgmt));
	}

	GetEmbeddedLink(key: string): Observable<any> {
		return this.http.get(`${this.serverData().rest.embedded_linK + '/' + key}`).pipe(catchError(this.errorMgmt));
	}

	GetSettingsData(): Observable<any> {
		return this.http.get(`${this.serverData().rest.settings}`).pipe(catchError(this.errorMgmt));
	}

	GetAccess(): Observable<AccessResponse> {
		return this.http.get(`${this.serverData().rest.access}`).pipe(catchError(this.errorMgmt));
	}

	PostImageData(data: ImageData): Observable<any> {
		return this.http.post(`${this.serverData().post.image}`, data).pipe(catchError(this.errorMgmt));
	}

	PostStatsData<T>(data: T): Observable<unknown> {
		return this.http
			.post(`${this.serverData().post.stats}`, { ...data, session_key: this.getSessionKey() })
			.pipe(catchError(this.errorMgmt));
	}

	PostData(data, key, options: { suppressErrorModal?: boolean } = {}): Observable<any> {
		let context = new HttpContext();

		if (options.suppressErrorModal) {
			context = context.set(SUPPRESS_ERROR_MODAL, true);
		}

		return this.http.post(`${this.serverData().post.data}/${key}`, data, { context }).pipe(catchError(this.errorMgmt));
	}

	PostEmailData(data): Observable<any> {
		return this.http.post(`${this.serverData().post.email}`, data).pipe(catchError(this.errorMgmt));
	}

	PostFeedbackData(data: FeedbackData): Observable<FeedbackData> {
		return this.http.post(`${this.serverData().post.feedback}`, data).pipe(catchError(this.errorMgmt));
	}

	PostActionData(data: ActionData): Observable<object> {
		return this.http.post(`${this.serverData().post.action}`, data).pipe(catchError(this.errorMgmt));
	}

	PostRetailerAction(data: RetailerClickAction & { device: 'desktop' | 'mobile' }): Observable<{ success: boolean }> {
		return this.http.post(this.serverData().post.retailer_action, data).pipe(catchError(this.errorMgmt));
	}

	PutSessionData(data, key, options: { suppressErrorModal?: boolean } = {}): Observable<any> {
		let context = new HttpContext();

		if (options.suppressErrorModal) {
			context = context.set(SUPPRESS_ERROR_MODAL, true);
		}
		return this.http.put(`${this.serverData().put.sessionData}/${key}`, data, { context }).pipe(catchError(this.errorMgmt));
	}

	PostModelData<T>(data: T): Observable<any> {
		return this.http.post(this.serverData().post.model_glb, data).pipe(catchError(this.errorMgmt));
	}

	PostSendVerificationCode(data: SendCodeData): Observable<VerificationResponse> {
		return this.http.post(this.serverData().post.send_code, data).pipe(catchError(this.errorMgmt));
	}

	PostVerifyCode(data: VerifyCodeData): Observable<VerificationResponse> {
		const options = { context: new HttpContext().set(DISABLE_GLOBAL_EXCEPTION_HANDLING, true) };
		return this.http.post(this.serverData().post.verify_code, data, options).pipe(catchError(this.errorMgmt));
	}

	deleteModelData(name: string): Observable<any> {
		return this.http.delete(`${this.serverData().delete.model_glb}/${name}`).pipe(catchError(this.errorMgmt));
	}

	getCountryCodeFromAPI(): Observable<{ country_code: string }> {
		return this.http.get(this.serverData().rest.country_code).pipe(catchError(this.errorMgmt));
	}

	getPrice(sessionKey: string): Observable<PriceDetails> {
		return this.http.get(this.serverData().rest.price(sessionKey)).pipe(catchError(this.errorMgmt));
	}

	CreatePaymentIntent(sessionData: SessionData): Observable<{ clientSecret: string; amount: number; currency: string }> {
		return this.http.post<PaymentIntent>(this.serverData().post.payment_intent, sessionData).pipe(catchError(this.errorMgmt));
	}

	// Error handling
	errorMgmt(error: HttpErrorResponse): Observable<any> {
		let errorMessage: string;

		if (error.error instanceof ErrorEvent) {
			// Get client-side error
			errorMessage = error.error.message;
		} else {
			// Get server-side error
			errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
		}
		console.error(errorMessage);
		return throwError(errorMessage);
	}

	private isAppleDevice(): boolean {
		return navigator.vendor === 'Apple Computer, Inc.';
	}

	private getRetailerKey(): string | null {
		return this.sessionStorageService.getSession(STORAGE_NAMES.zip_embed_key);
	}

	private getSessionKey(): string | null {
		return (
			this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_session) ||
			this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_temp_session)
		);
	}
}
